import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { classNames } from '../../utils/style';
import { useParams } from '../../utils/useParams';
import { DefaultBsComponent } from '../../types/module';
import { ComparisonContext } from './ComparisonContext';

export interface ComparisonTab extends DefaultBsComponent {
  name: string;
  type: string;
  className?: string;
  children: ReactNode;
  defaultActive?: boolean;
  isSmall?: boolean;
}
export const Tab = ({
  name,
  type,
  className,
  children,
  defaultActive,
  isSmall,
  as: Component = 'button',
}: ComparisonTab) => {
  const [params, setParams] = useParams();
  const handle = (e: any) => {
    if (e?.code !== 'Enter' && typeof e?.code !== 'undefined') return;

    setParams(type, name);
  };

  return (
    <Component
      className={classNames(
        'ComparaisonTableTab',
        'p-12 text-12  border-blue-51',
        'box-border',
        (params?.[type] === name || (!params?.[type] && defaultActive)) &&
          'bg-white',
        !(params?.[type] === name || (!params?.[type] && defaultActive)) &&
          isSmall &&
          'text-blue-101',
        isSmall
          ? 'border-b last:border-0 h-40 whitespace-nowrap'
          : 'border-r last:border-0 xl:w-[198px] md:w-[130px] lg:w-[180px] px-24 py-[27px]',

        className,
      )}
      tabIndex={Component === 'button' ? 0 : null}
      onKeyDown={Component === 'button' ? handle : null}
      onClick={Component === 'button' ? handle : null}
    >
      {children}
    </Component>
  );
};

export interface ComparisonTabs {
  className?: string;
  children: ReactNode;
  isSmall?: boolean;
}
export const Tabs = ({ className, children, isSmall }: ComparisonTabs) => {
  const ref = useRef<HTMLDivElement>(null);
  const refBoxTabs = useRef<HTMLDivElement>(null);
  const [height, setheight] = useState<number | null>(null);
  const [width, setwidth] = useState<number | null>(null);
  const context = useContext(ComparisonContext);
  const [tabs, setTabs] = useState<number>(0);

  useEffect(() => {
    const elt: any = refBoxTabs.current;
    const tabsLength: any = refBoxTabs.current?.querySelectorAll(
      '.ComparaisonTableTab',
    )?.length;

    const observer = new ResizeObserver((entries) => {
      if (isSmall || tabsLength !== tabs) {
        const _width = entries?.[0]?.contentRect.width - 0.5;
        const _height = entries?.[0]?.contentRect.height;

        setheight(_width);
        setwidth(_height);

        setTabs(tabsLength);
      }
    });

    observer.observe(elt);

    return () => observer.unobserve(elt);
  }, [height, isSmall, tabs, width]);

  return (
    <div
      className="ComparaisonTabs ml-5"
      style={
        isSmall
          ? {
              width: width || 'auto',
              height: height || 'auto',
              position: 'relative',
            }
          : undefined
      }
    >
      <div
        style={
          isSmall && width
            ? {
                top: -width - (!context?.sticky ? 2 : 4),
              }
            : undefined
        }
        className={classNames('relative flex justify-end', className)}
      >
        <div
          ref={ref}
          className={classNames(
            'relative ',
            'w-fit',
            isSmall && '-rotate-90 origin-bottom-right',
          )}
        >
          <div
            ref={refBoxTabs}
            className={classNames(
              'boxTabs',
              'flex',
              isSmall && 'flex-col bg-blue-21 border-l-blue-51 border-r-blue-0',
              'border-blue-51 border overflow-hidden box-border',
              !context?.sticky && 'rounded-18',
              context?.sticky && isSmall && 'border-xl-0',
              context?.sticky && !isSmall && 'border-y-0',
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
